import React, { useState, useEffect } from 'react';
import { supabase } from '../../services/supabase';
import { formatDateTimeEST } from '../../utils/timezoneUtils';
import '../../styles/GameDateEditor.css';

const GameDateEditor = ({ game, isOpen, onClose, onSave }) => {
  const [dateTime, setDateTime] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (game && game.scheduled_date) {
      const date = new Date(game.scheduled_date);
      
      const offset = date.getTimezoneOffset() * 60 * 1000;
      const localDate = new Date(date.getTime() - offset);
      
      const year = localDate.getUTCFullYear();
      const month = String(localDate.getUTCMonth() + 1).padStart(2, '0');
      const day = String(localDate.getUTCDate()).padStart(2, '0');
      const hours = String(localDate.getUTCHours()).padStart(2, '0');
      const minutes = String(localDate.getUTCMinutes()).padStart(2, '0');
      
      setDateTime(`${year}-${month}-${day}T${hours}:${minutes}`);
    } else {
      setDateTime('');
    }
    setError(null);
  }, [game, isOpen]);

  const handleSave = async () => {
    if (!dateTime) {
      setError('Please select a date and time');
      return;
    }

    setLoading(true);
    try {
      const [datePart, timePart] = dateTime.split('T');
      const [year, month, day] = datePart.split('-').map(Number);
      const [hours, minutes] = timePart.split(':').map(Number);
      
      const localDate = new Date(year, month - 1, day, hours, minutes, 0);
      const offset = localDate.getTimezoneOffset() * 60 * 1000;
      const utcDate = new Date(localDate.getTime() + offset);
      const isoString = utcDate.toISOString();

      const { error: updateError } = await supabase
        .from('games')
        .update({ scheduled_date: isoString })
        .eq('id', game.id);

      if (updateError) throw updateError;

      setError(null);
      onSave();
      onClose();
    } catch (err) {
      setError('Failed to update game date: ' + (err.message || 'Unknown error'));
      console.error('Error updating game date:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleClear = async () => {
    setLoading(true);
    try {
      const { error: updateError } = await supabase
        .from('games')
        .update({ scheduled_date: null })
        .eq('id', game.id);

      if (updateError) throw updateError;

      setDateTime('');
      setError(null);
      onSave();
      onClose();
    } catch (err) {
      setError('Failed to clear game date: ' + (err.message || 'Unknown error'));
      console.error('Error clearing game date:', err);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen || !game) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content game-date-editor" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>Set Game Date & Time</h3>
          <button className="close-btn" onClick={onClose}>×</button>
        </div>

        <div className="modal-body">
          <div className="game-info">
            <p><strong>Week {game.week}</strong></p>
          </div>

          <div className="form-group">
            <label htmlFor="dateTime">Date & Time</label>
            <input
              id="dateTime"
              type="datetime-local"
              value={dateTime}
              onChange={(e) => setDateTime(e.target.value)}
              className="date-input"
              disabled={loading}
            />
            <small>Format: YYYY-MM-DD HH:MM</small>
          </div>

          {error && (
            <div className="error-message">{error}</div>
          )}

          {dateTime && (
            <div className="preview">
              <p><strong>Scheduled for:</strong></p>
              <p>{formatDateTimeEST(new Date(dateTime).toISOString())}</p>
            </div>
          )}
        </div>

        <div className="modal-footer">
          <button 
            className="btn-secondary"
            onClick={onClose}
            disabled={loading}
          >
            Cancel
          </button>
          <button 
            className="btn-danger"
            onClick={handleClear}
            disabled={loading || !game.scheduled_date}
          >
            Clear Date
          </button>
          <button 
            className="btn-primary"
            onClick={handleSave}
            disabled={loading || !dateTime}
          >
            {loading ? 'Saving...' : 'Save Date'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default GameDateEditor;
